/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { ChangeDetectorRef, Component, OnDestroy } from "@angular/core";
import { AbstractModalComponent } from "../../../common/commondialog/abstract-modal.component";
import { ConnectionServerModel } from "../../../common/model/connection-server-model";
import { FeatureConfigs } from "../../../common/model/feature-configs";
import { PreDataSetModel } from "../../../common/model/pre-data-set-model";
import { clientUtil, TranslateService, Logger } from "@html-core";
import { ModalDialogService } from "../../../common/commondialog/dialog.service";
import { LocalStorageService, LocalLogService } from "@html-core";
import { ViewClientModel } from "../../../common/model/viewclient-model";
import { LogFileService } from "../../../../native-shared/common/log-file.service";

@Component({
   standalone: false,
   selector: "pre-setting-dialog",
   templateUrl: "./pre-setting-dialog.component.html",
   styles: [
      `
         /deep/ .modal.show .modal-dialog {
            width: 451.89px;
         }
      `
   ]
})
export class PreSettingDialogComponent extends AbstractModalComponent implements OnDestroy {
   private adminSettings;
   private onCollectDataKillSwitch;
   private isAnonymousLoginEnabled;
   private isDataSharingEnabled;
   private isManagedByPolicy = true;
   private isCollectLogEnabled;
   private isCollectLogSupport;

   public logPath: string;
   public toggleSwitches;
   public killSwitches;
   public fileExtensionList;
   public defaultAppMap = {};
   public filteredAppsInfo;
   public logLevel: number = Logger.LEVEL_INFO;

   constructor(
      private connectionServerModel: ConnectionServerModel,
      private featureConfigs: FeatureConfigs,
      private preDataSetModel: PreDataSetModel,
      private modalDialogService: ModalDialogService,
      private translate: TranslateService,
      private localStorageService: LocalStorageService,
      private localLogService: LocalLogService,
      private viewClientModel: ViewClientModel,
      private logFileService: LogFileService,
      private changeDetector: ChangeDetectorRef
   ) {
      super();
      this.adminSettings = this.connectionServerModel.googleCommonAdminSettings || {};
      this.isCollectLogSupport = clientUtil.isChromeClient() || clientUtil.isIwaClient();
      this.setUp();
   }

   ngOnDestroy() {
      this.onDestroy();
   }

   private isSettingEditable = (id) => {
      if (!!this.adminSettings && this.adminSettings[id] !== undefined) {
         if (!this.adminSettings["editable"]) {
            // if that setting has been set by google admin, but google admin has not set key "editale"
            // use default behavior for that setting, which is client cannot edit that setting
            return false;
         } else {
            if (this.adminSettings["editable"][id] === undefined || this.adminSettings["editable"][id] === false) {
               // if that setting has been set by google admin, but google admin key "editale" has not that
               // setting or set false for that setting, then client cannot edit that setting
               return false;
            } else {
               // if google admin set "editable" for true for that setting, client can edit that setting
               return true;
            }
         }
      } else {
         // if google admin has not set or google admin not set that setting, client behavior is the same as without google
         // admin setting, client can set that setting.
         return true;
      }
   };

   public isSettingGrayOut = (id) => {
      return !this.isSettingEditable(id);
   };

   public isSwitchDisabled = (switchItem) => {
      let result = false;
      switch (switchItem.id) {
         case "anonymous_login": {
            result = this.isSettingGrayOut("enableAnonymousLogin");
            break;
         }
         case "data_sharing": {
            result = this.isSettingGrayOut("allowDataSharing");
            break;
         }
         default: {
            break;
         }
      }
      return result;
   };

   private onDestroy = () => {
      this.unbindKillSwitches();
   };

   /**
    * kill switch can take affect in real time.
    */
   private _bindKillSwitch = (switchName, key, onChange?) => {
      const handle = (killSwitchOn) => {
         setTimeout(() => {
            this.killSwitches[key] = killSwitchOn;
            if (typeof onChange === "function") {
               onChange();
            }
         });
      };
      this.featureConfigs.registerListener("KillSwitch-" + switchName, handle);
      return handle;
   };

   /**
    * kill switch can take affect in real time.
    */
   private _unbindKillSwitch = (switchName, handle) => {
      this.featureConfigs.unregisterListener("KillSwitch-" + switchName, handle);
   };

   /**
    * kill switch can take affect in real time.
    * can close sub dialog if adding close to dialog Service.
    */
   private bindKillSwitches = () => {
      this.killSwitches = {};
      this.onCollectDataKillSwitch = this._bindKillSwitch("AllowDataSharing", "data_sharing");
   };

   /**
    * kill switch can take affect in real time.
    */
   private unbindKillSwitches = () => {
      this._unbindKillSwitch("AllowDataSharing", this.onCollectDataKillSwitch);
      this.onCollectDataKillSwitch = undefined;
   };

   public isTheLastActiveItem = (id) => {
      let lastItem;
      for (const item of this.toggleSwitches) {
         if (item.isSupported) {
            lastItem = item;
         }
      }
      return lastItem.id === id;
   };

   private getTrustPolicy = (): Promise<any> => {
      return new Promise((resolve, reject) => {
         if (typeof window.chromeClient.getTrustPolicy !== "function") {
            reject();
         } else {
            window.chromeClient
               .getTrustPolicy()
               .then((trustedPolicy) => {
                  resolve(trustedPolicy);
               })
               .catch(reject);
         }
      });
   };

   private setUp = async () => {
      this.bindKillSwitches();
      this.updateStatus();
      if (this.isCollectLogSupport) {
         if (await this.updateCollectLogStatus()) {
            this.updateLogLevel();
         }
      }
      try {
         const trustPolicy = await this.getTrustPolicy();
         this.isManagedByPolicy = !!trustPolicy;
         Logger.info("read trusted model for policy, policy exist=" + this.isManagedByPolicy);
         if (this.isManagedByPolicy) {
            Logger.debug("trust policy: " + JSON.stringify(trustPolicy));
         }
      } catch (error) {
         this.isManagedByPolicy = true;
         Logger.error("Failed to get trusted model, lock the setting");
      }

      this.toggleSwitches = [
         {
            id: "data_sharing",
            label: this.translate._T("DATA_SHARING_T"),
            text: this.translate._T("WARN_DATA_SHARING"),
            isSupported: true,
            isEnabled: this.isDataSharingEnabled,
            toggle: this.toggleDataSharing
         },
         {
            id: "anonymous_login",
            label: this.translate._T("ANONYMOUS_LOGIN_T"),
            isSupported: clientUtil.isChromeClient(),
            isEnabled: this.isAnonymousLoginEnabled,
            toggle: this.toggleAnonymousLogin
         },
         {
            id: "collect_log",
            label: this.translate._T("collect_log_t"),
            text: "",
            isSupported: this.isCollectLogSupport,
            isEnabled: this.isCollectLogEnabled,
            toggle: this.toggleCollectLog
         },
         {
            id: "trusted_apps",
            label: this.translate._T("TRUSTED_APPS"),
            text: this.isSettingGrayOut("addTrustedApps") ? this.translate._T("TRUSTED_APPS_DETAIL") : "",
            isSupported: clientUtil.isChromeClient(),
            isSettingGrayOut: this.isManagedByPolicy,
            hiddenBtn: true
         },
         {
            id: "file_association_default_app",
            label: this.translate._T("CHANGE_DEFAULT_FILE_ASSOCIATION_APP_TITLE"),
            isSupported: clientUtil.isChromeOrIwaClient(),
            hiddenBtn: true
         }
      ];
   };

   private updateStatus = () => {
      // For SR 2994646: Put the fetching from adminSettings together with the fetching from preDataSetModel
      [
         {
            adminName: "allowDataSharing",
            modelName: "dataSharingAllowed",
            propName: "isDataSharingEnabled"
         },
         {
            adminName: "enableAnonymousLogin",
            modelName: "anonymousLogin",
            propName: "isAnonymousLoginEnabled"
         }
      ].forEach(({ adminName, modelName, propName }) => {
         // Only editable settings can take value from the preDataSetModel
         if (this.isSettingEditable(adminName)) {
            this[propName] = this.preDataSetModel.settingData[modelName];
         }
         // Non-editable settings and settings with no value in preDataSetModel are taken from adminSettings
         if (this[propName] === undefined) {
            this[propName] = this.adminSettings[adminName];
            this.preDataSetModel.setDataValue(modelName, this.isDataSharingEnabled);
         }
      });
   };

   private updateCollectLogStatus = () => {
      return new Promise((resolve) => {
         this.isCollectLogEnabled = this.localStorageService.get("isCollectLogEnabled") === "true";
         this.logFileService.getLogPathFromStorage().then((path) => {
            if (path) {
               this.logPath = path;
               resolve(true);
            } else {
               this.isCollectLogEnabled = false;
               this.preDataSetModel.setDataValue("isCollectLogEnabled", false);
               resolve(false);
            }
         });
      });
   };

   private toggleDataSharing = () => {
      this.isDataSharingEnabled = !this.isDataSharingEnabled;
      this.preDataSetModel.setDataValue("dataSharingAllowed", this.isDataSharingEnabled);
   };

   private toggleAnonymousLogin = () => {
      this.isAnonymousLoginEnabled = !this.isAnonymousLoginEnabled;
      this.preDataSetModel.setDataValue("anonymousLogin", this.isAnonymousLoginEnabled);
   };

   private toggleCollectLog = () => {
      const togglePos = this._getTogglePosById("collect_log");
      this.isCollectLogEnabled = !this.isCollectLogEnabled;
      this.preDataSetModel.setDataValue("isCollectLogEnabled", this.isCollectLogEnabled);
      if (this.isCollectLogEnabled) {
         //turn on
         this.localLogService
            .startLogging()
            .then((info: any) => {
               this.logPath = this.logFileService.updateLogFileInStorage(info);
            })
            .catch((e) => {
               Logger.exception(e);
               this.isCollectLogEnabled = false;
               this.toggleSwitches[togglePos].isEnabled = this.isCollectLogEnabled;
            });
         this.updateLogLevel();
      } else {
         // turn off
         this.logFileService.clearLogInfoInStorage();
         this.logPath = null;
         this.localLogService.endLogging();
      }
   };

   private updateLogLevel = () => {
      let logLevel = this.localStorageService.get("logLevel");
      let logLevelNumber: number;
      if (logLevel) {
         logLevelNumber = Number(logLevel);
      } else {
         logLevelNumber = Logger.LEVEL_INFO;
      }
      //@ts-ignore
      this.logLevel = this.logFileService.formatLogLevel(logLevelNumber);
      setTimeout(() => {
         Logger.setLogLevel(logLevelNumber);
         this.viewClientModel.logLevel = logLevelNumber;
      }, 10);
   };

   public changeLogLevel = () => {
      setTimeout(() => {
         Logger.info("Change log level to " + this.logLevel);
         const logLevel = this.logFileService.formatLogLevel(this.logLevel);
         Logger.setLogLevel(logLevel);
         this.localStorageService.set("logLevel", logLevel);
      });
   };

   public changeLogFile = () => {
      this.localLogService.startLogging("changeFile").then((info) => {
         if (info) {
            let path = this.logFileService.updateLogFileInStorage(info);
            this.logPath = path;
         }
      });
   };

   public showAddTrustedAppsDialog = () => {
      this.modalDialogService.showSettingTrustedApps();
   };

   public showDefaultAppDialog = () => {
      this.modalDialogService.showSettingFileAssociation();
   };

   public onKeypress = (evt) => {
      if (!!evt && (evt.keyCode === 32 || evt.keyCode === 13)) {
         // Space key or enter key is pressed.
         if (evt.target.children && evt.target.children[0]) {
            $(evt.target.children[0]).trigger("click");
         }
         // Prevent default behavior.
         evt.preventDefault();
      }
   };

   private _getTogglePosById = (id) => {
      if (!id || !this.toggleSwitches) {
         Logger.error("The id is invalid, should be an item in setting list");
         return -1;
      }

      for (let i = 0; i < this.toggleSwitches.length; i++) {
         if (this.toggleSwitches[i].id === id) {
            Logger.info("Find " + id + " in setting list.");
            return i;
         }
      }
      Logger.error("Didn't find " + id + " in setting list.");
      return -1;
   };
}
